import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// Extend the Axios request config to include our custom property
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _tokenRefreshed?: boolean;
}
import type { ApiResponse, LoginRequest, LoginResponse, RegisterRequest, User, SwitchOrganizationRequest, MagicLinkRequest, CustomerPortalRegistrationRequest, CustomerPortalAuthResponse, MagicLinkVerifyResponse, Organization, UpdateProfileRequest, UpdatePasswordRequest, ForgotPasswordRequest, ResetPasswordRequest } from '../types/auth';
import type { OrganizationSettingsResponse, UpdateOrganizationSettingsRequest, OrganizationDetails, AdminOrganizationSummary, BusinessHolidayResponse, CreateHolidayRequest } from '../types/organizations';
import { refreshTokenManager } from '../lib/refreshTokenManager';
import type { HeldEmailResponse, HeldEmailReleaseResponse } from '../types/heldEmails';
import type {
  TicketListResponse,
  CustomerTicketActionsResponse,
  CustomerTicketScope,
  TicketDetailResponse,
  TrashedTicketResponse,
  RedactTextResponse,
  TicketStatsResponse,
  CustomerTicketStatsResponse,
  CustomerInsightsResponse,
  CustomerActivityResponse,
  CreateTicketRequest,
  UpdateTicketRequest,
  AddCommentRequest,
  TicketCommentResponse,
  TicketFilterRequest,
  TicketHistoryEntry,
  MergeTicketRequest,
  MergeTicketPreviewResponse,
  MergeTicketResponse,
  TicketResponse,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  ReorderCategoriesRequest,
  CategoryListResponse,
  CategoryDetailResponse,
  PortalSuggestedArticle,
  UserSummary,
  StatusLookupResponse,
  PriorityLookupResponse,
  ArticleStatusLookupResponse,
  LookupsResponse,
  PortalCategoryResponse,
  CustomerPortalLookupsResponse,
  DashboardOverviewResponse,
  CategoryReportResponse,
  TicketTrendDataPoint,
  ParticipantResponse,
  EligibleParticipantResponse,
  TicketFollowersResponse,
  FollowTicketResponse
} from '../types/tickets';
import type {
  CustomerListResponse,
  CustomerDetailResponse,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerFilterRequest,
  CustomerStatsResponse,
  ErasureResponse
} from '../types/customers';
import type {
  ArticleDto,
  ArticleListResponse,
  ArticleStatsDto,
  CreateArticleRequest,
  UpdateArticleRequest,
  ArticleFilterRequest,
  ArticleSearchRequest,
  PublishArticleRequest,
  ArticleRevisionSummary,
  ArticleRevisionResponse,
  ArticleDraftResponse,
  SaveArticleDraftRequest,
  PublishArticleDraftRequest,
  ArticleNeedsAttentionResponse,
  SearchInsightsResponse,
  BulkStatusUpdateRequest,
  BulkDeleteRequest,
  MoveToCollectionRequest,
  MoveToCollectionResponse,
  ArticleSearchResponse,
  ArticleFullTextSearchResponse,
  CustomerPortalArticleDto,
  CustomerPortalArticleListResponse,
  CustomerPortalArticleDetailDto,
  CustomerPortalSearchResponse,
  TicketArticleLinkResponse,
  TicketArticleLinksResponse,
  LinkArticleToTicketRequest,
  UpdateTicketArticleLinkRequest,
  ArticleTicketLinksResponse,
  CustomerPortalLinkedArticlesResponse,
  SubmitArticleFeedbackRequest,
  ArticleFeedbackResponse,
  ArticleFeedbackSummary,
  ArticleFeedbackListResponse
} from '../types/articles';
import type {
  ArticleCollectionListResponse,
  ArticleCollectionResponse,
  CreateArticleCollectionRequest,
  UpdateArticleCollectionRequest,
  ReorderArticleCollectionsRequest,
  PortalCollectionsResponse,
} from '../types/articleCollections';
import type {
  NotificationResponse,
  NotificationListResponse,
  NotificationCountResponse,
  NotificationFilterRequest,
  MarkNotificationsReadRequest,
} from '../types/notifications';
import type {
  AutomationRuleListResponse,
  AutomationRuleDetailResponse,
  CreateAutomationRuleRequest,
  UpdateAutomationRuleRequest,
  ToggleAutomationRuleRequest,
  RuleOrderItem,
  AutomationRuleFilterRequest,
  AutomationRuleLogListResponse,
  AutomationRuleLogFilterRequest,
} from '../types/automationRules';
import type {
  UserDto,
  UserDetailResponse,
  UserListResponse,
  UserStatsDto,
  CreateUserRequest,
  UpdateUserRequest,
  UserFilterRequest,
  UpdateUserRolesRequest,
  ChangePasswordRequest,
  UpdateUserOrganizationRequest,
  UpdateUserOrganizationResponse,
  RemoveUserFromOrganizationResponse,
  OpenTicketHandoff
} from '../types/users';
import { clearAllCustomerThemes } from '../lib/portal-loaders';
import { apiConfig } from '../lib/config';
import { getApiErrorMessage } from '../lib/apiErrors';

class ApiService {
  private api: AxiosInstance;
  private isRedirecting = false; // Flag to prevent multiple redirects on 401

  private isCustomerPortalContext(): boolean {
    // Check if current path is a customer portal route
    const path = window.location.pathname;
    return path.startsWith('/org/') && path.includes('/portal') || path.startsWith('/portal');
  }

  private getLoginUrl(): string {
    if (this.isCustomerPortalContext()) {
      // For customer portal, determine if it's path-based or subdomain-based
      const path = window.location.pathname;
      if (path.startsWith('/org/')) {
        // Extract org slug from path like /org/acme/portal/...
        const match = path.match(/^\/org\/([^/]+)/);
        if (match) {
          return `/org/${match[1]}/portal/login`;
        }
      }
      // Default to subdomain-based portal login
      return '/portal/login';
    }
    // Default to main app login
    return '/login';
  }

  private getPortalOrganizationInfo(): { organizationId: string } | null {
    // Try to get organization info from global portal context
    const portalContext = window.__PORTAL_CONTEXT__;
    if (portalContext?.organizationInfo?.organizationId) {
      return {
        organizationId: portalContext.organizationInfo.organizationId
      };
    }

    // Fallback: try to extract from URL and use validation endpoint result
    const path = window.location.pathname;
    if (path.startsWith('/org/')) {
      const match = path.match(/^\/org\/([^/]+)/);
      if (match) {
        // For now, we'll rely on the portal context being set by the route loader
        // This method will be called during API requests after the context is available
        const existingContext = window.__PORTAL_CONTEXT__;
        if (existingContext?.organizationInfo?.organizationId) {
          return {
            organizationId: existingContext.organizationInfo.organizationId
          };
        }
      }
    }

    return null;
  }

  private getCustomerTokenKey(organizationId: string): string {
    return `customerToken_${organizationId}`;
  }

  private getCustomerToken(organizationId?: string): string | null {
    if (!organizationId) {
      const orgInfo = this.getPortalOrganizationInfo();
      if (!orgInfo) return null;
      organizationId = orgInfo.organizationId;
    }
    return localStorage.getItem(this.getCustomerTokenKey(organizationId));
  }

  // Public method to expose token management for AuthContext
  public setCustomerTokenForOrganization(token: string, organizationId: string): void {
    localStorage.setItem(this.getCustomerTokenKey(organizationId), token);
  }

  private clearCustomerToken(organizationId: string): void {
    localStorage.removeItem(this.getCustomerTokenKey(organizationId));
  }

  private clearAllCustomerTokens(): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('customerToken_')) {
        localStorage.removeItem(key);
      }
    });
  }

  private validateTokenOrganization(token: string, expectedOrganizationId: string): boolean {
    try {
      // Decode JWT token (simple base64 decode, no signature verification needed for client-side)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tokenOrganizationId = payload.organization_id;
      return tokenOrganizationId === expectedOrganizationId;
    } catch (error) {
      console.error('Error validating token organization:', error);
      return false;
    }
  }

  /**
   * Gets the current device's push subscription endpoint.
   * Used to exclude this device from silent badge sync pushes.
   */
  private async getDeviceEndpoint(): Promise<string | null> {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return null;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription?.endpoint ?? null;
    } catch {
      return null;
    }
  }

  constructor() {
    this.api = axios.create({
      baseURL: apiConfig.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      async (config: CustomAxiosRequestConfig) => {
        // Use context-aware token selection
        let token;
        if (this.isCustomerPortalContext()) {
          // For customer portal, get organization-specific customer token
          const orgInfo = this.getPortalOrganizationInfo();
          if (orgInfo) {
            token = this.getCustomerToken(orgInfo.organizationId);

            // Validate that the token belongs to the current organization
            if (token && !this.validateTokenOrganization(token, orgInfo.organizationId)) {
              console.warn('Customer token organization mismatch, clearing token');
              this.clearCustomerToken(orgInfo.organizationId);
              token = null;
            }
          }

          // No fallback to the staff token: it belongs to whichever organization the staff member is signed in to,
          // so on another organization's portal the API rejects it (organization mismatch) and the public knowledge
          // base bounces them to sign in. Portal endpoints only take customer tokens; without one the request is
          // anonymous, which a public knowledge base allows.
        } else {
          // For admin portal, only use authToken (don't fall back to customerToken which could be for wrong org)
          token = localStorage.getItem('authToken');
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add organization ID header for customer portal requests
        if (this.isCustomerPortalContext()) {
          const organizationInfo = this.getPortalOrganizationInfo();
          if (organizationInfo?.organizationId) {
            config.headers['X-Organization-ID'] = organizationInfo.organizationId;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;

        // Don't handle 401 errors for login/register endpoints - let them be handled normally
        if (original.url?.includes('/auth/login') || original.url?.includes('/auth/register') || original.url?.includes('/MagicLinkAuth/')) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          
          // Check if we're in customer portal context
          const isCustomerPortal = this.isCustomerPortalContext();
          
          if (!isCustomerPortal) {
            // For admin portal, try to refresh token using global manager
            try {
              const result = await refreshTokenManager.refreshToken();
              if (result.success && result.token) {
                // Retry original request with new token
                original.headers.Authorization = `Bearer ${result.token}`;
                return this.api(original);
              }
            } catch (error) {
              console.warn('Token refresh failed in response interceptor:', error);
            }
          }

          // If refresh failed or this is customer portal, clear tokens and redirect
          this.endSession();
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Ends the current session in this tab: clears its tokens, tells the auth context, and redirects to the right
   * login page. Used when a request is rejected and can't be refreshed, and when the server announces the
   * session was revoked (SignalR "SessionRevoked" - access removed, account deactivated, password reset).
   */
  endSession(): void {
    if (this.isCustomerPortalContext()) {
      // Clear organization-specific customer tokens
      const orgInfo = this.getPortalOrganizationInfo();
      if (orgInfo) {
        this.clearCustomerToken(orgInfo.organizationId);
      } else {
        // Fallback: clear all customer tokens and legacy token
        this.clearAllCustomerTokens();
        localStorage.removeItem('customerToken');
      }
      localStorage.removeItem('customerData');
      clearAllCustomerThemes();
    } else {
      // Clear only admin tokens
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('adminTheme');
    }

    // Skip redirect if we're already on a login page or auto-login page
    const currentPath = window.location.pathname;
    const isOnLoginPage = currentPath.includes('/login') ||
                          currentPath.includes('/portal/auto-login');

    if (!isOnLoginPage && !this.isRedirecting) {
      this.isRedirecting = true;
      // Dispatch event to notify auth context before redirect
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      // Small delay to allow state cleanup before redirect
      setTimeout(() => {
        window.location.href = this.getLoginUrl();
      }, 100);
    }
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await this.api.post<ApiResponse<LoginResponse>>('/auth/login', credentials);
      return response.data;
    } catch (error) {
      return this.handleError<LoginResponse>(error);
    }
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await this.api.post<ApiResponse<LoginResponse>>('/auth/register', userData);
      return response.data;
    } catch (error) {
      return this.handleError<LoginResponse>(error);
    }
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await this.api.post<ApiResponse<LoginResponse>>('/auth/refresh', {
        refreshToken,
      });
      return response.data;
    } catch (error) {
      return this.handleError<LoginResponse>(error);
    }
  }

  async logout(refreshToken?: string | null): Promise<ApiResponse<boolean>> {
    try {
      // The refresh token is what the server revokes - without it, logout ends nothing server-side and the
      // token stays usable for 90 days
      const response = await this.api.post<ApiResponse<boolean>>('/auth/logout', refreshToken ? { refreshToken } : undefined);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response = await this.api.get<ApiResponse<User>>('/auth/me');
      return response.data;
    } catch (error) {
      return this.handleError<User>(error);
    }
  }

  async switchOrganization(request: SwitchOrganizationRequest): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await this.api.post<ApiResponse<LoginResponse>>('/auth/switch-organization', request);
      return response.data;
    } catch (error) {
      return this.handleError<LoginResponse>(error);
    }
  }

  async forgotPassword(request: ForgotPasswordRequest): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>('/auth/forgot-password', request);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async resetPassword(request: ResetPasswordRequest): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>('/auth/reset-password', request);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Profile endpoints
  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response = await this.api.get<ApiResponse<User>>('/profile');
      return response.data;
    } catch (error) {
      return this.handleError<User>(error);
    }
  }

  async updateProfile(data: UpdateProfileRequest): Promise<ApiResponse<User>> {
    try {
      const response = await this.api.put<ApiResponse<User>>('/profile', data);
      return response.data;
    } catch (error) {
      return this.handleError<User>(error);
    }
  }

  async updatePassword(data: UpdatePasswordRequest): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.put<ApiResponse<boolean>>('/profile/password', data);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getAdminOrganizations(): Promise<ApiResponse<AdminOrganizationSummary[]>> {
    try {
      const response = await this.api.get<ApiResponse<AdminOrganizationSummary[]>>('/profile/admin-organizations');
      return response.data;
    } catch (error) {
      return this.handleError<AdminOrganizationSummary[]>(error);
    }
  }

  // Customer Portal Magic Link endpoints
  async requestMagicLink(request: MagicLinkRequest): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.api.post<ApiResponse<{ message: string }>>('/MagicLinkAuth/request', request);
      return response.data;
    } catch (error) {
      return this.handleError<{ message: string }>(error);
    }
  }

  async registerCustomerAndRequestMagicLink(request: CustomerPortalRegistrationRequest): Promise<ApiResponse<{ expiresInMinutes: number; customerName: string }>> {
    try {
      const response = await this.api.post<ApiResponse<{ expiresInMinutes: number; customerName: string }>>('/MagicLinkAuth/register', request);
      return response.data;
    } catch (error) {
      return this.handleError<{ expiresInMinutes: number; customerName: string }>(error);
    }
  }

  async authenticateWithMagicLink(token: string, organizationId: string): Promise<ApiResponse<CustomerPortalAuthResponse>> {
    try {
      const response = await this.api.post<ApiResponse<CustomerPortalAuthResponse>>('/MagicLinkAuth/authenticate', { 
        token, 
        organizationId 
      });
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalAuthResponse>(error);
    }
  }

  async verifyMagicLink(token: string, organizationId: string): Promise<ApiResponse<MagicLinkVerifyResponse>> {
    try {
      const response = await this.api.post<ApiResponse<MagicLinkVerifyResponse>>('/MagicLinkAuth/verify', { 
        token, 
        organizationId 
      });
      return response.data;
    } catch (error) {
      return this.handleError<MagicLinkVerifyResponse>(error);
    }
  }

  // Customer Portal endpoints
  async getCustomerPortalTickets(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: number[];
    priority?: number[];
    sortOrder?: 'asc' | 'desc';
    scope?: CustomerTicketScope;
  }): Promise<ApiResponse<TicketListResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.search) queryParams.append('search', params.search);
      if (params?.status && params.status.length > 0) {
        params.status.forEach(s => queryParams.append('status', s.toString()));
      }
      if (params?.priority && params.priority.length > 0) {
        params.priority.forEach(p => queryParams.append('priority', p.toString()));
      }
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.scope) queryParams.append('scope', params.scope);

      const queryString = queryParams.toString();
      const url = queryString ? `/customer-portal/tickets?${queryString}` : '/customer-portal/tickets';
      const response = await this.api.get<ApiResponse<TicketListResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<TicketListResponse>(error);
    }
  }

  async getCustomerTicketStats(): Promise<ApiResponse<CustomerTicketStatsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerTicketStatsResponse>>('/customer-portal/tickets/stats');
      return response.data;
    } catch (error) {
      return this.handleError<CustomerTicketStatsResponse>(error);
    }
  }

  async getCustomerInsights(): Promise<ApiResponse<CustomerInsightsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerInsightsResponse>>('/customer-portal/tickets/insights');
      return response.data;
    } catch (error) {
      return this.handleError<CustomerInsightsResponse>(error);
    }
  }

  async getCustomerRecentActivity(): Promise<ApiResponse<CustomerActivityResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerActivityResponse[]>>('/customer-portal/tickets/activity');
      return response.data;
    } catch (error) {
      return this.handleError<CustomerActivityResponse[]>(error);
    }
  }

  async createCustomerTicket(ticket: CreateTicketRequest): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.post<ApiResponse<TicketDetailResponse>>('/customer-portal/tickets', ticket);
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  async submitCustomerTicket(ticketData: { subject: string; description: string; priority: number; categoryId?: string; attachments?: File[]; participantCustomerIds?: string[] }): Promise<ApiResponse<{ ticketId: string; ticketNumber: number; subject: string }>> {
    try {
      // Always send as multipart/form-data to match backend [Consumes("multipart/form-data")] attribute
      const formData = new FormData();
      formData.append('subject', ticketData.subject);
      formData.append('description', ticketData.description);
      formData.append('priority', ticketData.priority.toString());

      if (ticketData.categoryId) {
        formData.append('categoryId', ticketData.categoryId);
      }

      // Append all attachment files if present
      if (ticketData.attachments && ticketData.attachments.length > 0) {
        ticketData.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      // Append participant customer IDs if present
      if (ticketData.participantCustomerIds && ticketData.participantCustomerIds.length > 0) {
        ticketData.participantCustomerIds.forEach(id => {
          formData.append('participantCustomerIds', id);
        });
      }

      const response = await this.api.post<ApiResponse<{ ticketId: string; ticketNumber: number; subject: string }>>(
        '/customer-portal/tickets/submit',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<{ ticketId: string; ticketNumber: number; subject: string }>(error);
    }
  }

  async uploadCustomerAttachment(ticketId: string, file: File): Promise<ApiResponse<{ id: string; fileName: string; fileSize: number }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await this.api.post<ApiResponse<{ id: string; fileName: string; fileSize: number }>>(
        `/customer-portal/tickets/${ticketId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<{ id: string; fileName: string; fileSize: number }>(error);
    }
  }

  async getCustomerTicket(ticketId: string): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.get<ApiResponse<TicketDetailResponse>>(`/customer-portal/tickets/${ticketId}`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  async getCustomerPortalTicketArticles(ticketId: string): Promise<ApiResponse<CustomerPortalLinkedArticlesResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalLinkedArticlesResponse>>(`/customer-portal/tickets/${ticketId}/articles`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalLinkedArticlesResponse>(error);
    }
  }

  async addCustomerComment(ticketId: string, content: string, attachments?: File[]): Promise<ApiResponse<TicketCommentResponse>> {
    try {
      // Always send as multipart/form-data to match backend [Consumes("multipart/form-data")] attribute
      const formData = new FormData();
      formData.append('content', content);

      // Append all attachment files if present
      if (attachments && attachments.length > 0) {
        attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      const response = await this.api.post<ApiResponse<TicketCommentResponse>>(
        `/customer-portal/tickets/${ticketId}/comments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<TicketCommentResponse>(error);
    }
  }

  async updateCustomerTicket(ticketId: string, updates: { priority?: number; categoryId?: string }): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.put<ApiResponse<TicketDetailResponse>>(`/customer-portal/tickets/${ticketId}`, updates);
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  async uploadCustomerCommentAttachment(ticketId: string, commentId: string, file: File): Promise<ApiResponse<{ id: string; fileName: string; fileSize: number }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.api.post<ApiResponse<{ id: string; fileName: string; fileSize: number }>>(
        `/customer-portal/tickets/${ticketId}/comments/${commentId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<{ id: string; fileName: string; fileSize: number }>(error);
    }
  }

  async downloadCustomerPortalAttachment(attachmentId: string): Promise<Blob> {
    try {
      const response = await this.api.get(
        `/customer-portal/tickets/attachments/${attachmentId}`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      throw new Error(await this.getBlobErrorMessage(error, 'Failed to download attachment'));
    }
  }

  async getCustomerTicketActions(ticketId: string): Promise<ApiResponse<CustomerTicketActionsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerTicketActionsResponse>>(`/customer-portal/tickets/${ticketId}/available-actions`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerTicketActionsResponse>(error);
    }
  }

  async resolveCustomerTicket(ticketId: string, comment?: string): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.post<ApiResponse<TicketDetailResponse>>(
        `/customer-portal/tickets/${ticketId}/resolve`,
        { comment }
      );
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  async reopenCustomerTicket(ticketId: string, comment?: string): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.post<ApiResponse<TicketDetailResponse>>(
        `/customer-portal/tickets/${ticketId}/reopen`,
        { comment }
      );
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  // Categories a customer may pick: active, not internal, in tree order with paths
  async getCustomerPortalCategories(): Promise<ApiResponse<PortalCategoryResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<PortalCategoryResponse[]>>('/customer-portal/tickets/categories');
      return response.data;
    } catch (error) {
      return this.handleError<PortalCategoryResponse[]>(error);
    }
  }

  // Knowledge base articles from the collection a category suggests, for the new-ticket form's "might help" panel
  async getCustomerPortalSuggestedArticles(categoryId: string, take = 5): Promise<ApiResponse<PortalSuggestedArticle[]>> {
    try {
      const response = await this.api.get<ApiResponse<PortalSuggestedArticle[]>>(
        `/customer-portal/tickets/categories/${categoryId}/suggested-articles?take=${take}`
      );
      return response.data;
    } catch (error) {
      return this.handleError<PortalSuggestedArticle[]>(error);
    }
  }

  async getCustomerPortalLookups(): Promise<ApiResponse<CustomerPortalLookupsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalLookupsResponse>>('/customer-portal/tickets/lookups');
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalLookupsResponse>(error);
    }
  }

  // Customer Portal Participants (CCs)
  async getCustomerPortalTicketParticipants(ticketId: string): Promise<ApiResponse<ParticipantResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<ParticipantResponse[]>>(`/customer-portal/tickets/${ticketId}/participants`);
      return response.data;
    } catch (error) {
      return this.handleError<ParticipantResponse[]>(error);
    }
  }

  async addCustomerPortalTicketParticipant(ticketId: string, customerId: string): Promise<ApiResponse<ParticipantResponse>> {
    try {
      const response = await this.api.post<ApiResponse<ParticipantResponse>>(`/customer-portal/tickets/${ticketId}/participants`, { customerId });
      return response.data;
    } catch (error) {
      return this.handleError<ParticipantResponse>(error);
    }
  }

  async removeCustomerPortalTicketParticipant(ticketId: string, participantCustomerId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/customer-portal/tickets/${ticketId}/participants/${participantCustomerId}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getCustomerPortalEligibleParticipants(ticketId: string, search?: string): Promise<ApiResponse<EligibleParticipantResponse[]>> {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await this.api.get<ApiResponse<EligibleParticipantResponse[]>>(`/customer-portal/tickets/${ticketId}/participants/eligible${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<EligibleParticipantResponse[]>(error);
    }
  }

  async getCustomerPortalEligibleParticipantsForNewTicket(search?: string): Promise<ApiResponse<EligibleParticipantResponse[]>> {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await this.api.get<ApiResponse<EligibleParticipantResponse[]>>(`/customer-portal/tickets/participants/search${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<EligibleParticipantResponse[]>(error);
    }
  }

  // Customer Portal Knowledge Base endpoints
  // A collection includes its sections; sortBy "sortOrder" reads a collection in its curated order
  async getCustomerPortalArticles(params?: {
    page?: number;
    pageSize?: number;
    collectionId?: string;
    // Only articles in no collection (the portal's "Other articles")
    uncategorized?: boolean;
    isFeatured?: boolean;
    searchQuery?: string;
    sortBy?: string;
    sortDescending?: boolean;
  }): Promise<ApiResponse<CustomerPortalArticleListResponse>> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, value.toString());
          }
        });
      }

      const response = await this.api.get<ApiResponse<CustomerPortalArticleListResponse>>(`/customer-portal/knowledge-base/articles?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleListResponse>(error);
    }
  }

  async getCustomerPortalArticle(articleId: string): Promise<ApiResponse<CustomerPortalArticleDetailDto>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalArticleDetailDto>>(`/customer-portal/knowledge-base/articles/${articleId}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleDetailDto>(error);
    }
  }

  async searchCustomerPortalArticles(params: {
    query: string;
    collectionId?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDescending?: boolean;
  }): Promise<ApiResponse<CustomerPortalSearchResponse>> {
    try {
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });

      const response = await this.api.get<ApiResponse<CustomerPortalSearchResponse>>(`/customer-portal/knowledge-base/search?${searchParams.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalSearchResponse>(error);
    }
  }

  // Search-as-you-type: the best few matches for what's typed so far (not logged as a search)
  async suggestCustomerPortalArticles(query: string, take: number = 6): Promise<ApiResponse<CustomerPortalArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalArticleDto[]>>(
        `/customer-portal/knowledge-base/suggest?query=${encodeURIComponent(query)}&take=${take}`
      );
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleDto[]>(error);
    }
  }

  // Articles related to free text (the new-ticket form's subject) - by its significant words, not all of them
  async getCustomerPortalRelatedArticles(text: string, take: number = 5): Promise<ApiResponse<CustomerPortalArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalArticleDto[]>>(
        `/customer-portal/knowledge-base/related?text=${encodeURIComponent(text)}&take=${take}`
      );
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleDto[]>(error);
    }
  }

  // Counts that a visitor opened a result of a logged search (searchId from the search's first page)
  async recordCustomerPortalSearchClick(searchId: string, articleId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>(`/customer-portal/knowledge-base/searches/${searchId}/click`, { articleId });
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Collections and sections that hold published public articles, with counts
  async getCustomerPortalCollections(): Promise<ApiResponse<PortalCollectionsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<PortalCollectionsResponse>>('/customer-portal/knowledge-base/collections');
      return response.data;
    } catch (error) {
      return this.handleError<PortalCollectionsResponse>(error);
    }
  }

  async getCustomerPortalPopularArticles(count?: number): Promise<ApiResponse<CustomerPortalArticleDto[]>> {
    try {
      const params = count ? `?count=${count}` : '';
      const response = await this.api.get<ApiResponse<CustomerPortalArticleDto[]>>(`/customer-portal/knowledge-base/popular${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleDto[]>(error);
    }
  }

  async getCustomerPortalFeaturedArticles(): Promise<ApiResponse<CustomerPortalArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalArticleDto[]>>('/customer-portal/knowledge-base/featured');
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleDto[]>(error);
    }
  }

  // Customer Portal Profile endpoints
  async getCustomerProfile(): Promise<ApiResponse<import('../types/profile').CustomerProfile>> {
    try {
      const response = await this.api.get<ApiResponse<import('../types/profile').CustomerProfile>>('/customer-portal/profile');
      return response.data;
    } catch (error) {
      return this.handleError<import('../types/profile').CustomerProfile>(error);
    }
  }

  async updateCustomerProfile(data: import('../types/profile').UpdateProfileRequest): Promise<ApiResponse<import('../types/profile').CustomerProfile>> {
    try {
      const response = await this.api.put<ApiResponse<import('../types/profile').CustomerProfile>>('/customer-portal/profile', data);
      return response.data;
    } catch (error) {
      return this.handleError<import('../types/profile').CustomerProfile>(error);
    }
  }

  // Ticket endpoints
  async getTickets(filter: TicketFilterRequest = {}): Promise<ApiResponse<TicketListResponse>> {
    try {
      const params = new URLSearchParams();

      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Handle array parameters (for multi-select filters)
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await this.api.get<ApiResponse<TicketListResponse>>(`/tickets?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketListResponse>(error);
    }
  }

  // Multi-org ticket endpoint for unified helpdesk view
  async getAllOrganizationTickets(filter: TicketFilterRequest = {}): Promise<ApiResponse<TicketListResponse>> {
    try {
      const params = new URLSearchParams();

      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Handle array parameters (for multi-select filters like organizationIds)
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await this.api.get<ApiResponse<TicketListResponse>>(`/tickets/all?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketListResponse>(error);
    }
  }

  async getTicket(id: string): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.get<ApiResponse<TicketDetailResponse>>(`/tickets/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  async createTicket(ticket: CreateTicketRequest): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      // Always send as multipart/form-data to match backend [Consumes("multipart/form-data")] attribute
      const formData = new FormData();
      formData.append('subject', ticket.subject);
      formData.append('description', ticket.description);
      formData.append('priority', ticket.priority.toString());
      formData.append('customerId', ticket.customerId);

      if (ticket.categoryId) formData.append('categoryId', ticket.categoryId);
      if (ticket.assignedToId) formData.append('assignedToId', ticket.assignedToId);
      if (ticket.dueAt) formData.append('dueAt', ticket.dueAt);
      if (ticket.targetOrganizationId) formData.append('targetOrganizationId', ticket.targetOrganizationId);

      // Append all attachment files if present
      if (ticket.attachments && ticket.attachments.length > 0) {
        ticket.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      // Append participant customer IDs if present
      if (ticket.participantCustomerIds && ticket.participantCustomerIds.length > 0) {
        ticket.participantCustomerIds.forEach(id => {
          formData.append('participantCustomerIds', id);
        });
      }

      const response = await this.api.post<ApiResponse<TicketDetailResponse>>(
        '/tickets',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  async updateTicket(id: string, updates: UpdateTicketRequest): Promise<ApiResponse<TicketDetailResponse>> {
    try {
      const response = await this.api.put<ApiResponse<TicketDetailResponse>>(`/tickets/${id}`, updates);
      return response.data;
    } catch (error) {
      return this.handleError<TicketDetailResponse>(error);
    }
  }

  // Moves the ticket to the trash (admins) - restorable until it's purged
  async deleteTicket(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/tickets/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async restoreTicket(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>(`/tickets/${id}/restore`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Permanently deletes a ticket that's already in the trash
  async purgeTicket(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/tickets/${id}/permanent`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Permanently redacts text from a comment (admins): every character becomes ▇, here and in the ticket's history,
  // audit entries and emails. Matched exactly
  async redactComment(ticketId: string, commentId: string, text: string): Promise<ApiResponse<RedactTextResponse>> {
    try {
      const response = await this.api.post<ApiResponse<RedactTextResponse>>(
        `/tickets/${ticketId}/comments/${commentId}/redact`,
        { text }
      );
      return response.data;
    } catch (error) {
      return this.handleError<RedactTextResponse>(error);
    }
  }

  // Same as redactComment, for the ticket's description
  async redactTicketDescription(ticketId: string, text: string): Promise<ApiResponse<RedactTextResponse>> {
    try {
      const response = await this.api.post<ApiResponse<RedactTextResponse>>(`/tickets/${ticketId}/description/redact`, { text });
      return response.data;
    } catch (error) {
      return this.handleError<RedactTextResponse>(error);
    }
  }

  // Email from archived customers waiting for an admin (Org Settings → Held Emails)
  async getHeldEmails(organizationId?: string): Promise<ApiResponse<HeldEmailResponse[]>> {
    try {
      const url = organizationId ? `/held-emails?organizationIds=${organizationId}` : '/held-emails';
      const response = await this.api.get<ApiResponse<HeldEmailResponse[]>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<HeldEmailResponse[]>(error);
    }
  }

  // Restores the archived customer who sent it and processes the email (new ticket or comment)
  async releaseHeldEmail(id: string): Promise<ApiResponse<HeldEmailReleaseResponse>> {
    try {
      const response = await this.api.post<ApiResponse<HeldEmailReleaseResponse>>(`/held-emails/${id}/release`);
      return response.data;
    } catch (error) {
      return this.handleError<HeldEmailReleaseResponse>(error);
    }
  }

  // Permanently deletes a held email
  async discardHeldEmail(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/held-emails/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getTrashedTickets(organizationId?: string): Promise<ApiResponse<TrashedTicketResponse[]>> {
    try {
      const url = organizationId ? `/tickets/trash?organizationIds=${organizationId}` : '/tickets/trash';
      const response = await this.api.get<ApiResponse<TrashedTicketResponse[]>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<TrashedTicketResponse[]>(error);
    }
  }

  async addComment(ticketId: string, comment: AddCommentRequest): Promise<ApiResponse<TicketCommentResponse>> {
    try {
      // Always send as multipart/form-data to match backend [Consumes("multipart/form-data")] attribute
      const formData = new FormData();
      formData.append('content', comment.content);

      if (comment.isInternal !== undefined) {
        formData.append('isInternal', comment.isInternal.toString());
      }

      // Append all attachment files if present
      if (comment.attachments && comment.attachments.length > 0) {
        comment.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }

      const response = await this.api.post<ApiResponse<TicketCommentResponse>>(
        `/tickets/${ticketId}/comments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<TicketCommentResponse>(error);
    }
  }

  async getTicketHistory(ticketId: string): Promise<ApiResponse<TicketHistoryEntry[]>> {
    try {
      const response = await this.api.get<ApiResponse<TicketHistoryEntry[]>>(`/tickets/${ticketId}/history`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketHistoryEntry[]>(error);
    }
  }

  async getMergePreview(
    sourceTicketId: string,
    targetTicketId: string,
    organizationId: string
  ): Promise<ApiResponse<MergeTicketPreviewResponse>> {
    try {
      const response = await this.api.get<ApiResponse<MergeTicketPreviewResponse>>(
        `/tickets/${sourceTicketId}/merge/preview`,
        { params: { targetTicketId, organizationId } }
      );
      return response.data;
    } catch (error) {
      return this.handleError<MergeTicketPreviewResponse>(error);
    }
  }

  async mergeTicket(
    sourceTicketId: string,
    request: MergeTicketRequest,
    organizationId: string
  ): Promise<ApiResponse<MergeTicketResponse>> {
    try {
      const response = await this.api.post<ApiResponse<MergeTicketResponse>>(
        `/tickets/${sourceTicketId}/merge`,
        request,
        { params: { organizationId } }
      );
      return response.data;
    } catch (error) {
      return this.handleError<MergeTicketResponse>(error);
    }
  }

  async getTicketStats(): Promise<ApiResponse<TicketStatsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<TicketStatsResponse>>('/tickets/stats');
      return response.data;
    } catch (error) {
      return this.handleError<TicketStatsResponse>(error);
    }
  }

  async getDashboardOverview(): Promise<ApiResponse<DashboardOverviewResponse>> {
    try {
      const response = await this.api.get<ApiResponse<DashboardOverviewResponse>>('/reports/dashboard');
      return response.data;
    } catch (error) {
      return this.handleError<DashboardOverviewResponse>(error);
    }
  }

  async getDashboardOverviewAllOrganizations(organizationIds?: string[]): Promise<ApiResponse<DashboardOverviewResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationIds && organizationIds.length > 0) {
        organizationIds.forEach(id => params.append('organizationIds', id));
      }
      const queryString = params.toString();
      const url = queryString ? `/reports/dashboard/all?${queryString}` : '/reports/dashboard/all';
      const response = await this.api.get<ApiResponse<DashboardOverviewResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<DashboardOverviewResponse>(error);
    }
  }

  // Tickets created in the range by category, own and rolled up with subcategories
  async getCategoryReport(organizationId: string, fromDate: string, toDate: string): Promise<ApiResponse<CategoryReportResponse>> {
    try {
      const params = new URLSearchParams({ organizationId, fromDate, toDate });
      const response = await this.api.get<ApiResponse<CategoryReportResponse>>(`/reports/categories?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<CategoryReportResponse>(error);
    }
  }

  async getTicketTrends(fromDate?: string, toDate?: string): Promise<ApiResponse<TicketTrendDataPoint[]>> {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);

      const url = `/reports/tickets/trends-with-completion${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await this.api.get<ApiResponse<TicketTrendDataPoint[]>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<TicketTrendDataPoint[]>(error);
    }
  }

  // Ticket Participants (CCs) - Staff Portal
  async getTicketParticipants(ticketId: string, organizationId?: string): Promise<ApiResponse<ParticipantResponse[]>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.get<ApiResponse<ParticipantResponse[]>>(`/tickets/${ticketId}/participants${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<ParticipantResponse[]>(error);
    }
  }

  async addTicketParticipant(ticketId: string, customerId: string, organizationId?: string): Promise<ApiResponse<ParticipantResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<ParticipantResponse>>(`/tickets/${ticketId}/participants${params}`, { customerId });
      return response.data;
    } catch (error) {
      return this.handleError<ParticipantResponse>(error);
    }
  }

  async addTicketParticipants(ticketId: string, customerIds: string[], organizationId?: string): Promise<ApiResponse<ParticipantResponse[]>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<ParticipantResponse[]>>(`/tickets/${ticketId}/participants/bulk${params}`, { customerIds });
      return response.data;
    } catch (error) {
      return this.handleError<ParticipantResponse[]>(error);
    }
  }

  async removeTicketParticipant(ticketId: string, customerId: string, organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.delete<ApiResponse<boolean>>(`/tickets/${ticketId}/participants/${customerId}${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getEligibleParticipants(ticketId: string, organizationId?: string, search?: string): Promise<ApiResponse<EligibleParticipantResponse[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (organizationId) queryParams.append('organizationId', organizationId);
      if (search) queryParams.append('search', search);
      const queryString = queryParams.toString();
      const url = queryString ? `/tickets/${ticketId}/participants/eligible?${queryString}` : `/tickets/${ticketId}/participants/eligible`;
      const response = await this.api.get<ApiResponse<EligibleParticipantResponse[]>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<EligibleParticipantResponse[]>(error);
    }
  }

  // Ticket Followers (Watchers) - Staff Portal only
  async getTicketFollowers(ticketId: string, organizationId?: string): Promise<ApiResponse<TicketFollowersResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.get<ApiResponse<TicketFollowersResponse>>(`/tickets/${ticketId}/followers${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketFollowersResponse>(error);
    }
  }

  async followTicket(ticketId: string, organizationId?: string): Promise<ApiResponse<FollowTicketResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<FollowTicketResponse>>(`/tickets/${ticketId}/followers/follow${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<FollowTicketResponse>(error);
    }
  }

  async unfollowTicket(ticketId: string, organizationId?: string): Promise<ApiResponse<FollowTicketResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<FollowTicketResponse>>(`/tickets/${ticketId}/followers/unfollow${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<FollowTicketResponse>(error);
    }
  }

  async toggleTicketFollow(ticketId: string, organizationId?: string): Promise<ApiResponse<FollowTicketResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<FollowTicketResponse>>(`/tickets/${ticketId}/followers/toggle${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<FollowTicketResponse>(error);
    }
  }

  // Customer endpoints
  async getCustomers(filters?: CustomerFilterRequest): Promise<ApiResponse<CustomerListResponse>> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            // Handle array values (e.g., organizationIds)
            if (Array.isArray(value)) {
              value.forEach(v => params.append(key, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }

      const response = await this.api.get<ApiResponse<CustomerListResponse>>(`/customers?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerListResponse>(error);
    }
  }

  async createCustomer(customer: CreateCustomerRequest, organizationId: string): Promise<ApiResponse<CustomerDetailResponse>> {
    try {
      const response = await this.api.post<ApiResponse<CustomerDetailResponse>>(
        `/customers?organizationId=${organizationId}`,
        customer
      );
      return response.data;
    } catch (error) {
      return this.handleError<CustomerDetailResponse>(error);
    }
  }

  async getCustomer(id: string): Promise<ApiResponse<CustomerDetailResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerDetailResponse>>(`/customers/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerDetailResponse>(error);
    }
  }

  async getCustomerByEmail(email: string, organizationId: string): Promise<ApiResponse<CustomerDetailResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerDetailResponse>>(
        `/customers/by-email/${encodeURIComponent(email)}?organizationId=${organizationId}`
      );
      return response.data;
    } catch (error) {
      return this.handleError<CustomerDetailResponse>(error);
    }
  }

  async updateCustomer(id: string, updates: UpdateCustomerRequest): Promise<ApiResponse<CustomerDetailResponse>> {
    try {
      const response = await this.api.put<ApiResponse<CustomerDetailResponse>>(`/customers/${id}`, updates);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerDetailResponse>(error);
    }
  }

  async archiveCustomer(id: string): Promise<ApiResponse<CustomerDetailResponse>> {
    try {
      const response = await this.api.post<ApiResponse<CustomerDetailResponse>>(`/customers/${id}/archive`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerDetailResponse>(error);
    }
  }

  // Erases an archived customer's personal data (GDPR) - their email, typed, confirms it. Irreversible
  async eraseCustomer(id: string, confirmEmail: string): Promise<ApiResponse<ErasureResponse>> {
    try {
      const response = await this.api.post<ApiResponse<ErasureResponse>>(`/customers/${id}/erase`, { confirmEmail });
      return response.data;
    } catch (error) {
      return this.handleError<ErasureResponse>(error);
    }
  }

  async restoreCustomer(id: string): Promise<ApiResponse<CustomerDetailResponse>> {
    try {
      const response = await this.api.post<ApiResponse<CustomerDetailResponse>>(`/customers/${id}/restore`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerDetailResponse>(error);
    }
  }

  // Permanent - only succeeds for a customer with no ticket history; others are archived instead
  async deleteCustomer(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/customers/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getCustomerStats(organizationIds?: string[]): Promise<ApiResponse<CustomerStatsResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationIds && organizationIds.length > 0) {
        organizationIds.forEach(id => params.append('organizationIds', id));
      }
      const url = params.toString() ? `/customers/stats?${params.toString()}` : '/customers/stats';
      const response = await this.api.get<ApiResponse<CustomerStatsResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerStatsResponse>(error);
    }
  }

  async getCustomerTickets(customerId: string): Promise<ApiResponse<TicketResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<TicketResponse[]>>(`/customers/${customerId}/tickets`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketResponse[]>(error);
    }
  }

  // Category endpoints (ticket categories - a tree, returned whole in display order, not paged)
  // Active categories only unless includeArchived - pickers must not offer archived ones
  async getCategories(organizationId?: string, options?: { includeArchived?: boolean }): Promise<ApiResponse<CategoryDetailResponse[]>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      if (options?.includeArchived) params.append('includeArchived', 'true');
      const url = params.toString() ? `/categories?${params.toString()}` : '/categories';
      const response = await this.api.get<ApiResponse<CategoryListResponse>>(url);
      return {
        ...response.data,
        data: response.data.data?.categories ?? []
      };
    } catch (error) {
      return this.handleError<CategoryDetailResponse[]>(error);
    }
  }

  async getCategory(id: string): Promise<ApiResponse<CategoryDetailResponse>> {
    try {
      const response = await this.api.get<ApiResponse<CategoryDetailResponse>>(`/categories/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<CategoryDetailResponse>(error);
    }
  }

  async createCategory(category: CreateCategoryRequest, organizationId?: string): Promise<ApiResponse<CategoryDetailResponse>> {
    try {
      const url = organizationId ? `/categories?organizationId=${organizationId}` : '/categories';
      const response = await this.api.post<ApiResponse<CategoryDetailResponse>>(url, category);
      return response.data;
    } catch (error) {
      return this.handleError<CategoryDetailResponse>(error);
    }
  }

  // Leave a field out to keep it; send null to clear a clearable one (see UpdateCategoryRequest)
  async updateCategory(id: string, updates: UpdateCategoryRequest, organizationId?: string): Promise<ApiResponse<CategoryDetailResponse>> {
    try {
      const url = organizationId ? `/categories/${id}?organizationId=${organizationId}` : `/categories/${id}`;
      const response = await this.api.put<ApiResponse<CategoryDetailResponse>>(url, updates);
      return response.data;
    } catch (error) {
      return this.handleError<CategoryDetailResponse>(error);
    }
  }

  // Sets one parent's subcategory order (the top level when parentCategoryId is null); returns the whole tree
  async reorderCategories(request: ReorderCategoriesRequest, organizationId?: string): Promise<ApiResponse<CategoryDetailResponse[]>> {
    try {
      const url = organizationId ? `/categories/reorder?organizationId=${organizationId}` : '/categories/reorder';
      const response = await this.api.post<ApiResponse<CategoryListResponse>>(url, request);
      return { ...response.data, data: response.data.data?.categories ?? [] };
    } catch (error) {
      return this.handleError<CategoryDetailResponse[]>(error);
    }
  }

  async archiveCategory(id: string, organizationId?: string): Promise<ApiResponse<CategoryDetailResponse>> {
    try {
      const url = organizationId ? `/categories/${id}/archive?organizationId=${organizationId}` : `/categories/${id}/archive`;
      const response = await this.api.post<ApiResponse<CategoryDetailResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<CategoryDetailResponse>(error);
    }
  }

  async restoreCategory(id: string, organizationId?: string): Promise<ApiResponse<CategoryDetailResponse>> {
    try {
      const url = organizationId ? `/categories/${id}/restore?organizationId=${organizationId}` : `/categories/${id}/restore`;
      const response = await this.api.post<ApiResponse<CategoryDetailResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<CategoryDetailResponse>(error);
    }
  }

  // Permanent - only succeeds for a category nothing uses; others are archived instead
  async deleteCategory(id: string, organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const url = organizationId ? `/categories/${id}?organizationId=${organizationId}` : `/categories/${id}`;
      const response = await this.api.delete<ApiResponse<boolean>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // User management endpoints
  async getUsers(filters?: UserFilterRequest, organizationId?: string): Promise<ApiResponse<UserListResponse>> {
    try {
      const params = new URLSearchParams();

      // Add organizationId if provided for multi-org support
      if (organizationId) params.append('organizationId', organizationId);

      if (filters) {
        if (filters.searchQuery) params.append('searchQuery', filters.searchQuery);
        if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
        if (filters.roles?.length) filters.roles.forEach(role => params.append('roles', role));
        if (filters.createdAfter) params.append('createdAfter', filters.createdAfter);
        if (filters.createdBefore) params.append('createdBefore', filters.createdBefore);
        if (filters.lastLoginAfter) params.append('lastLoginAfter', filters.lastLoginAfter);
        if (filters.lastLoginBefore) params.append('lastLoginBefore', filters.lastLoginBefore);
        if (filters.page) params.append('page', filters.page.toString());
        if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDescending !== undefined) params.append('descending', filters.sortDescending.toString());
      }

      const url = params.toString() ? `/users?${params}` : '/users';
      const response = await this.api.get<ApiResponse<UserListResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<UserListResponse>(error);
    }
  }

  async getUser(id: string): Promise<ApiResponse<UserDetailResponse>> {
    try {
      const response = await this.api.get<ApiResponse<UserDetailResponse>>(`/users/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<UserDetailResponse>(error);
    }
  }

  async createUser(user: CreateUserRequest, organizationId?: string): Promise<ApiResponse<UserDto>> {
    try {
      const url = organizationId ? `/users?organizationId=${organizationId}` : '/users';
      const response = await this.api.post<ApiResponse<UserDto>>(url, user);
      return response.data;
    } catch (error) {
      return this.handleError<UserDto>(error);
    }
  }

  async updateUser(id: string, updates: UpdateUserRequest): Promise<ApiResponse<UserDto>> {
    try {
      const response = await this.api.put<ApiResponse<UserDto>>(`/users/${id}`, updates);
      return response.data;
    } catch (error) {
      return this.handleError<UserDto>(error);
    }
  }

  async deleteUser(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/users/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getUserStats(organizationId?: string): Promise<ApiResponse<UserStatsDto>> {
    try {
      const url = organizationId ? `/users/stats?organizationId=${organizationId}` : '/users/stats';
      const response = await this.api.get<ApiResponse<UserStatsDto>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<UserStatsDto>(error);
    }
  }

  async getUserRoles(id: string): Promise<ApiResponse<string[]>> {
    try {
      const response = await this.api.get<ApiResponse<string[]>>(`/users/${id}/roles`);
      return response.data;
    } catch (error) {
      return this.handleError<string[]>(error);
    }
  }

  async getAvailableRoles(): Promise<ApiResponse<string[]>> {
    try {
      const response = await this.api.get<ApiResponse<string[]>>('/users/available-roles');
      return response.data;
    } catch (error) {
      return this.handleError<string[]>(error);
    }
  }

  async updateUserRoles(id: string, roles: UpdateUserRolesRequest): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.put<ApiResponse<boolean>>(`/users/${id}/roles`, roles);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async changeUserPassword(id: string, passwordData: ChangePasswordRequest): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>(`/users/${id}/change-password`, passwordData);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async updateUserOrganization(id: string, orgData: UpdateUserOrganizationRequest, organizationId?: string): Promise<ApiResponse<UpdateUserOrganizationResponse>> {
    try {
      const url = organizationId ? `/users/${id}/organization?organizationId=${organizationId}` : `/users/${id}/organization`;
      const response = await this.api.put<ApiResponse<UpdateUserOrganizationResponse>>(url, orgData);
      return response.data;
    } catch (error) {
      return this.handleError<UpdateUserOrganizationResponse>(error);
    }
  }

  async removeUserFromOrganization(id: string, organizationId?: string, handoff?: OpenTicketHandoff): Promise<ApiResponse<RemoveUserFromOrganizationResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      if (handoff?.reassignOpenTicketsToUserId) params.append('reassignOpenTicketsToUserId', handoff.reassignOpenTicketsToUserId);
      if (handoff?.unassignOpenTickets) params.append('unassignOpenTickets', 'true');
      const query = params.toString();
      const url = query ? `/users/${id}/organization?${query}` : `/users/${id}/organization`;
      const response = await this.api.delete<ApiResponse<RemoveUserFromOrganizationResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<RemoveUserFromOrganizationResponse>(error);
    }
  }

  // Tickets assigned to a member that aren't closed - what deactivating or removing them hands off
  async getMemberOpenTicketCount(id: string, organizationId?: string): Promise<ApiResponse<number>> {
    try {
      const url = organizationId
        ? `/users/${id}/organization/open-ticket-count?organizationId=${organizationId}`
        : `/users/${id}/organization/open-ticket-count`;
      const response = await this.api.get<ApiResponse<number>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<number>(error);
    }
  }

  // Get users that can be assigned to tickets (Agents and Admins only)
  async getUsersForAssignment(organizationId?: string): Promise<ApiResponse<UserSummary[]>> {
    try {
      const url = organizationId
        ? `/tickets/assignable-users?organizationId=${organizationId}`
        : '/tickets/assignable-users';
      const response = await this.api.get<ApiResponse<UserSummary[]>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<UserSummary[]>(error);
    }
  }

  // Organization management endpoints
  async getOrganizations(): Promise<ApiResponse<Organization[]>> {
    try {
      const response = await this.api.get<ApiResponse<Organization[]>>('/organizations');
      return response.data;
    } catch (error) {
      return this.handleError<Organization[]>(error);
    }
  }

  async getOrganization(id: string): Promise<ApiResponse<Organization>> {
    try {
      const response = await this.api.get<ApiResponse<Organization>>(`/organizations/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<Organization>(error);
    }
  }

  async updateOrganization(id: string, updates: Partial<Organization>): Promise<ApiResponse<Organization>> {
    try {
      const response = await this.api.put<ApiResponse<Organization>>(`/organizations/${id}`, updates);
      return response.data;
    } catch (error) {
      return this.handleError<Organization>(error);
    }
  }

  // Settings endpoints
  async getSettings(): Promise<ApiResponse<unknown>> {
    try {
      const response = await this.api.get<ApiResponse<unknown>>('/settings');
      return response.data;
    } catch (error) {
      return this.handleError<unknown>(error);
    }
  }

  async updateSettings(settings: unknown): Promise<ApiResponse<unknown>> {
    try {
      const response = await this.api.put<ApiResponse<unknown>>('/settings', settings);
      return response.data;
    } catch (error) {
      return this.handleError<unknown>(error);
    }
  }

  // Attachment endpoints
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    try {
      const response = await this.api.get(`/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(await this.getBlobErrorMessage(error, 'Failed to download attachment'));
    }
  }

  // Permanently deletes an attachment and its stored file (admins in its organization)
  async deleteAttachment(attachmentId: string): Promise<ApiResponse<unknown>> {
    try {
      const response = await this.api.delete<ApiResponse<unknown>>(`/attachments/${attachmentId}`);
      return response.data;
    } catch (error) {
      return this.handleError<unknown>(error);
    }
  }

  async uploadAttachment(ticketId: string, file: File): Promise<ApiResponse<{ id: string; fileName: string; fileSize: number }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await this.api.post<ApiResponse<{ id: string; fileName: string; fileSize: number }>>(
        `/attachments/tickets/${ticketId}`, 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<{ id: string; fileName: string; fileSize: number }>(error);
    }
  }

  async uploadCommentAttachment(commentId: string, file: File): Promise<ApiResponse<{ id: string; fileName: string; fileSize: number }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.api.post<ApiResponse<{ id: string; fileName: string; fileSize: number }>>(
        `/attachments/comments/${commentId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return this.handleError<{ id: string; fileName: string; fileSize: number }>(error);
    }
  }

  async fetchAttachmentBlob(url: string): Promise<Blob> {
    const response = await this.api.get(url, { responseType: 'blob' });
    return response.data;
  }

  getAttachmentThumbnailUrl(attachmentId: string): string {
    return `/attachments/${attachmentId}/thumbnail`;
  }

  getAttachmentPreviewUrl(attachmentId: string): string {
    return `/attachments/${attachmentId}/preview`;
  }

  // Articles/Knowledge Base endpoints
  async getArticles(filter?: ArticleFilterRequest): Promise<ApiResponse<ArticleListResponse>> {
    try {
      const params = new URLSearchParams();
      if (filter) {
        Object.entries(filter).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(key, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }
      const response = await this.api.get<ApiResponse<ArticleListResponse>>(`/articles?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleListResponse>(error);
    }
  }

  async getArticle(id: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleDto>>(`/articles/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async createArticle(article: CreateArticleRequest): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>('/articles', article);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async updateArticle(id: string, updates: UpdateArticleRequest): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.put<ApiResponse<ArticleDto>>(`/articles/${id}`, updates);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async uploadArticleImage(file: File, organizationId?: string): Promise<ApiResponse<{ id: string; fileName: string; contentType: string; fileSize: number; url: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<{ id: string; fileName: string; contentType: string; fileSize: number; url: string }>>(
        `/articles/images/upload${params}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      return this.handleError<{ id: string; fileName: string; contentType: string; fileSize: number; url: string }>(error);
    }
  }

  async deleteArticle(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    try {
      const response = await this.api.delete<ApiResponse<{ deleted: boolean }>>(`/articles/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<{ deleted: boolean }>(error);
    }
  }

  async searchArticles(request: ArticleSearchRequest): Promise<ApiResponse<ArticleSearchResponse>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleSearchResponse>>('/articles/search', request);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleSearchResponse>(error);
    }
  }

  async getPublishedArticles(page: number = 1, pageSize: number = 20): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleDto[]>>(`/articles/published?page=${page}&pageSize=${pageSize}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  async getFeaturedArticles(): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleDto[]>>('/articles/featured');
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  // A collection's articles in reading order; its sections' articles too unless includeSections is false
  async getArticlesByCollection(collectionId: string, publishedOnly: boolean = true, includeSections: boolean = true): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleDto[]>>(
        `/articles/collection/${collectionId}?publishedOnly=${publishedOnly}&includeSections=${includeSections}`
      );
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  async getArticlesByAuthor(authorId: string): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleDto[]>>(`/articles/author/${authorId}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  async publishArticle(id: string, request: PublishArticleRequest): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/publish`, request);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async unpublishArticle(id: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/unpublish`, {});
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async archiveArticle(id: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/archive`, {});
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async restoreArticle(id: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/restore`, {});
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  // Revision history (newest first)
  async getArticleRevisions(id: string): Promise<ApiResponse<ArticleRevisionSummary[]>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleRevisionSummary[]>>(`/articles/${id}/revisions`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleRevisionSummary[]>(error);
    }
  }

  async getArticleRevision(id: string, revisionId: string): Promise<ApiResponse<ArticleRevisionResponse>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleRevisionResponse>>(`/articles/${id}/revisions/${revisionId}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleRevisionResponse>(error);
    }
  }

  // A draft article takes the revision's text at once; a published one gets it as its draft, to publish
  async restoreArticleRevision(id: string, revisionId: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/revisions/${revisionId}/restore`, {});
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  // A published article's unpublished changes (404 when there are none)
  async getArticleDraft(id: string): Promise<ApiResponse<ArticleDraftResponse>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleDraftResponse>>(`/articles/${id}/draft`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDraftResponse>(error);
    }
  }

  async saveArticleDraft(id: string, request: SaveArticleDraftRequest): Promise<ApiResponse<ArticleDraftResponse>> {
    try {
      const response = await this.api.put<ApiResponse<ArticleDraftResponse>>(`/articles/${id}/draft`, request);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDraftResponse>(error);
    }
  }

  async publishArticleDraft(id: string, request: PublishArticleDraftRequest): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/draft/publish`, request);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async discardArticleDraft(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/articles/${id}/draft`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // publishAt: an ISO time, at least a minute and at most a year ahead
  async scheduleArticle(id: string, publishAt: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/schedule`, { publishAt });
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async cancelArticleSchedule(id: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.delete<ApiResponse<ArticleDto>>(`/articles/${id}/schedule`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  async markArticleReviewed(id: string): Promise<ApiResponse<ArticleDto>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleDto>>(`/articles/${id}/review`, {});
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto>(error);
    }
  }

  // What customers searched for in the portal over the last `days`
  async getSearchInsights(organizationIds: string[] | undefined, days: number): Promise<ApiResponse<SearchInsightsResponse>> {
    try {
      const params = new URLSearchParams({ days: String(days) });
      organizationIds?.forEach((id) => params.append('organizationIds', id));
      const response = await this.api.get<ApiResponse<SearchInsightsResponse>>(`/articles/search-insights?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<SearchInsightsResponse>(error);
    }
  }

  async getArticlesNeedingAttention(organizationIds?: string[]): Promise<ApiResponse<ArticleNeedsAttentionResponse>> {
    try {
      const params = new URLSearchParams();
      organizationIds?.forEach((id) => params.append('organizationIds', id));
      const query = params.toString();
      const response = await this.api.get<ApiResponse<ArticleNeedsAttentionResponse>>(`/articles/needs-attention${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleNeedsAttentionResponse>(error);
    }
  }


  async recordArticleView(id: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await this.api.post<ApiResponse<{ message: string }>>(`/articles/${id}/view`, {});
      return response.data;
    } catch (error) {
      return this.handleError<{ message: string }>(error);
    }
  }

  async getArticleStats(organizationIds?: string[]): Promise<ApiResponse<ArticleStatsDto>> {
    try {
      const params = new URLSearchParams();
      if (organizationIds && organizationIds.length > 0) {
        organizationIds.forEach(id => params.append('organizationIds', id));
      }
      const url = params.toString() ? `/articles/statistics?${params.toString()}` : '/articles/statistics';
      const response = await this.api.get<ApiResponse<ArticleStatsDto>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleStatsDto>(error);
    }
  }

  async getMostViewedArticles(count: number = 10, since?: string, organizationIds?: string[]): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const params = new URLSearchParams();
      params.append('count', count.toString());
      if (since) {
        params.append('since', since);
      }
      if (organizationIds && organizationIds.length > 0) {
        organizationIds.forEach(id => params.append('organizationIds', id));
      }
      const response = await this.api.get<ApiResponse<ArticleDto[]>>(`/articles/most-viewed?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  async getRecentlyViewedArticles(count: number = 10, organizationIds?: string[]): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const params = new URLSearchParams();
      params.append('count', count.toString());
      if (organizationIds && organizationIds.length > 0) {
        organizationIds.forEach(id => params.append('organizationIds', id));
      }
      const response = await this.api.get<ApiResponse<ArticleDto[]>>(`/articles/recently-viewed?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  async fullTextSearch(query: string, publishedOnly: boolean = true, page: number = 1, pageSize: number = 20): Promise<ApiResponse<ArticleFullTextSearchResponse>> {
    try {
      const params = new URLSearchParams();
      params.append('query', query);
      params.append('publishedOnly', publishedOnly.toString());
      params.append('page', page.toString());
      params.append('pageSize', pageSize.toString());
      const response = await this.api.get<ApiResponse<ArticleFullTextSearchResponse>>(`/articles/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleFullTextSearchResponse>(error);
    }
  }

  async getSearchSuggestions(query: string, maxSuggestions: number = 5): Promise<ApiResponse<string[]>> {
    try {
      const response = await this.api.get<ApiResponse<string[]>>(`/articles/search/suggestions?query=${encodeURIComponent(query)}&maxSuggestions=${maxSuggestions}`);
      return response.data;
    } catch (error) {
      return this.handleError<string[]>(error);
    }
  }

  async bulkUpdateArticleStatus(request: BulkStatusUpdateRequest): Promise<ApiResponse<{ message: string; updatedCount: number }>> {
    try {
      const response = await this.api.post<ApiResponse<{ message: string; updatedCount: number }>>('/articles/bulk/status', request);
      return response.data;
    } catch (error) {
      return this.handleError<{ message: string; updatedCount: number }>(error);
    }
  }

  async bulkDeleteArticles(request: BulkDeleteRequest): Promise<ApiResponse<{ message: string; deletedCount: number }>> {
    try {
      const response = await this.api.post<ApiResponse<{ message: string; deletedCount: number }>>('/articles/bulk/delete', request);
      return response.data;
    } catch (error) {
      return this.handleError<{ message: string; deletedCount: number }>(error);
    }
  }

  // Files articles in a collection (or leaves them uncategorized when collectionId is null)
  async moveArticlesToCollection(request: MoveToCollectionRequest): Promise<ApiResponse<MoveToCollectionResponse>> {
    try {
      const response = await this.api.post<ApiResponse<MoveToCollectionResponse>>('/articles/bulk/move-collection', request);
      return response.data;
    } catch (error) {
      return this.handleError<MoveToCollectionResponse>(error);
    }
  }

  // Knowledge base collections (and their sections), in display order
  async getArticleCollections(organizationId?: string): Promise<ApiResponse<ArticleCollectionResponse[]>> {
    try {
      const url = organizationId ? `/article-collections?organizationId=${organizationId}` : '/article-collections';
      const response = await this.api.get<ApiResponse<ArticleCollectionListResponse>>(url);
      return { ...response.data, data: response.data.data?.collections ?? [] };
    } catch (error) {
      return this.handleError<ArticleCollectionResponse[]>(error);
    }
  }

  async createArticleCollection(request: CreateArticleCollectionRequest, organizationId?: string): Promise<ApiResponse<ArticleCollectionResponse>> {
    try {
      const url = organizationId ? `/article-collections?organizationId=${organizationId}` : '/article-collections';
      const response = await this.api.post<ApiResponse<ArticleCollectionResponse>>(url, request);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleCollectionResponse>(error);
    }
  }

  async updateArticleCollection(id: string, updates: UpdateArticleCollectionRequest, organizationId?: string): Promise<ApiResponse<ArticleCollectionResponse>> {
    try {
      const url = organizationId ? `/article-collections/${id}?organizationId=${organizationId}` : `/article-collections/${id}`;
      const response = await this.api.put<ApiResponse<ArticleCollectionResponse>>(url, updates);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleCollectionResponse>(error);
    }
  }

  async reorderArticleCollections(request: ReorderArticleCollectionsRequest, organizationId?: string): Promise<ApiResponse<ArticleCollectionResponse[]>> {
    try {
      const url = organizationId ? `/article-collections/reorder?organizationId=${organizationId}` : '/article-collections/reorder';
      const response = await this.api.post<ApiResponse<ArticleCollectionListResponse>>(url, request);
      return { ...response.data, data: response.data.data?.collections ?? [] };
    } catch (error) {
      return this.handleError<ArticleCollectionResponse[]>(error);
    }
  }

  // The reading order of the articles filed directly in a collection or section - exactly those articles (archived ones
  // excepted), or the API refuses it as stale
  async reorderCollectionArticles(collectionId: string, orderedIds: string[], organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<boolean>>(`/article-collections/${collectionId}/articles/reorder${params}`, { orderedIds });
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Moves the collection's articles to moveArticlesToCollectionId (or leaves them uncategorized), then deletes it
  async deleteArticleCollection(id: string, moveArticlesToCollectionId?: string | null, organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = new URLSearchParams();
      if (moveArticlesToCollectionId) params.append('moveArticlesToCollectionId', moveArticlesToCollectionId);
      if (organizationId) params.append('organizationId', organizationId);
      const query = params.toString();
      const response = await this.api.delete<ApiResponse<boolean>>(`/article-collections/${id}${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Ticket-Article Links endpoints
  async getTicketArticleLinks(ticketId: string, organizationId?: string): Promise<ApiResponse<TicketArticleLinksResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.get<ApiResponse<TicketArticleLinksResponse>>(`/tickets/${ticketId}/articles${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<TicketArticleLinksResponse>(error);
    }
  }

  async linkArticleToTicket(ticketId: string, request: LinkArticleToTicketRequest, organizationId?: string): Promise<ApiResponse<TicketArticleLinkResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.post<ApiResponse<TicketArticleLinkResponse>>(`/tickets/${ticketId}/articles${params}`, request);
      return response.data;
    } catch (error) {
      return this.handleError<TicketArticleLinkResponse>(error);
    }
  }

  async unlinkArticleFromTicket(ticketId: string, articleId: string, organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.delete<ApiResponse<boolean>>(`/tickets/${ticketId}/articles/${articleId}${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async updateTicketArticleLink(ticketId: string, articleId: string, request: UpdateTicketArticleLinkRequest, organizationId?: string): Promise<ApiResponse<TicketArticleLinkResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.put<ApiResponse<TicketArticleLinkResponse>>(`/tickets/${ticketId}/articles/${articleId}${params}`, request);
      return response.data;
    } catch (error) {
      return this.handleError<TicketArticleLinkResponse>(error);
    }
  }

  async suggestArticlesForTicket(ticketId: string, query: string, organizationId?: string, maxResults: number = 5): Promise<ApiResponse<ArticleDto[]>> {
    try {
      const params = new URLSearchParams({ q: query, maxResults: maxResults.toString() });
      if (organizationId) params.append('organizationId', organizationId);
      const response = await this.api.get<ApiResponse<ArticleDto[]>>(`/tickets/${ticketId}/articles/suggest?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleDto[]>(error);
    }
  }

  async getArticleTicketLinks(articleId: string, organizationId?: string): Promise<ApiResponse<ArticleTicketLinksResponse>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.get<ApiResponse<ArticleTicketLinksResponse>>(`/articles/${articleId}/tickets${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleTicketLinksResponse>(error);
    }
  }

  // Article Feedback endpoints
  async submitArticleFeedback(articleId: string, request: SubmitArticleFeedbackRequest): Promise<ApiResponse<ArticleFeedbackResponse>> {
    try {
      const response = await this.api.post<ApiResponse<ArticleFeedbackResponse>>(`/customer-portal/knowledge-base/articles/${articleId}/feedback`, request);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleFeedbackResponse>(error);
    }
  }

  async getMyArticleFeedback(articleId: string): Promise<ApiResponse<ArticleFeedbackResponse | null>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleFeedbackResponse | null>>(`/customer-portal/knowledge-base/articles/${articleId}/feedback`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleFeedbackResponse | null>(error);
    }
  }

  async getArticleFeedbackList(articleId: string, page: number = 1, pageSize: number = 20, organizationId?: string): Promise<ApiResponse<ArticleFeedbackListResponse>> {
    try {
      const params = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString() });
      if (organizationId) params.append('organizationId', organizationId);
      const response = await this.api.get<ApiResponse<ArticleFeedbackListResponse>>(`/articles/${articleId}/feedback?${params.toString()}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleFeedbackListResponse>(error);
    }
  }

  async getArticleFeedbackSummary(articleId: string, organizationId?: string): Promise<ApiResponse<ArticleFeedbackSummary>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.get<ApiResponse<ArticleFeedbackSummary>>(`/articles/${articleId}/feedback/summary${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<ArticleFeedbackSummary>(error);
    }
  }

  // Article Sort Order
  async bulkUpdateArticleSortOrder(items: { articleId: string; sortOrder: number }[], organizationId?: string): Promise<ApiResponse<number>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : '';
      const response = await this.api.put<ApiResponse<number>>(`/articles/bulk/sort-order${params}`, { items });
      return response.data;
    } catch (error) {
      return this.handleError<number>(error);
    }
  }

  // Article Slug Lookup
  async getCustomerPortalArticleBySlug(slug: string): Promise<ApiResponse<CustomerPortalArticleDetailDto>> {
    try {
      const response = await this.api.get<ApiResponse<CustomerPortalArticleDetailDto>>(`/customer-portal/knowledge-base/articles/by-slug/${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      return this.handleError<CustomerPortalArticleDetailDto>(error);
    }
  }

  // Organization settings endpoints
  async getOrganizationSettings(organizationId: string): Promise<ApiResponse<OrganizationSettingsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<OrganizationSettingsResponse>>(`/organizations/${organizationId}/settings`);
      return response.data;
    } catch (error) {
      return this.handleError<OrganizationSettingsResponse>(error);
    }
  }

  async updateOrganizationSettings(organizationId: string, settings: UpdateOrganizationSettingsRequest): Promise<ApiResponse<OrganizationSettingsResponse>> {
    try {
      const response = await this.api.put<ApiResponse<OrganizationSettingsResponse>>(`/organizations/${organizationId}/settings`, settings);
      return response.data;
    } catch (error) {
      return this.handleError<OrganizationSettingsResponse>(error);
    }
  }

  // Business Hours / Holiday endpoints
  async getHolidays(organizationId: string): Promise<ApiResponse<BusinessHolidayResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<BusinessHolidayResponse[]>>(`/organizations/${organizationId}/business-hours/holidays`);
      return response.data;
    } catch (error) {
      return this.handleError<BusinessHolidayResponse[]>(error);
    }
  }

  async addHoliday(organizationId: string, request: CreateHolidayRequest): Promise<ApiResponse<BusinessHolidayResponse>> {
    try {
      const response = await this.api.post<ApiResponse<BusinessHolidayResponse>>(`/organizations/${organizationId}/business-hours/holidays`, request);
      return response.data;
    } catch (error) {
      return this.handleError<BusinessHolidayResponse>(error);
    }
  }

  async removeHoliday(organizationId: string, holidayId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/organizations/${organizationId}/business-hours/holidays/${holidayId}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async batchUpdateHolidays(
    organizationId: string,
    changes: { toAdd: CreateHolidayRequest[]; toRemove: string[] }
  ): Promise<ApiResponse<BusinessHolidayResponse[]>> {
    try {
      const response = await this.api.post<ApiResponse<BusinessHolidayResponse[]>>(
        `/organizations/${organizationId}/business-hours/holidays/batch`,
        changes
      );
      return response.data;
    } catch (error) {
      return this.handleError<BusinessHolidayResponse[]>(error);
    }
  }

  async getCurrentOrganization(): Promise<ApiResponse<OrganizationDetails>> {
    try {
      const response = await this.api.get<ApiResponse<OrganizationDetails>>('/organizations/current');
      return response.data;
    } catch (error) {
      return this.handleError<OrganizationDetails>(error);
    }
  }

  async validateFromEmailAddress(fromEmailAddress: string, excludeId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = excludeId ? `?excludeId=${excludeId}` : '';
      const response = await this.api.get<ApiResponse<boolean>>(`/organizations/validate-from-email/${encodeURIComponent(fromEmailAddress)}${params}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Public Portal Key management endpoints
  async generatePublicPortalKey(organizationId: string): Promise<ApiResponse<{
    publicPortalKey: string;
    maskedPublicPortalKey: string;
    createdAt: string;
  }>> {
    try {
      const response = await this.api.post<ApiResponse<{
        publicPortalKey: string;
        maskedPublicPortalKey: string;
        createdAt: string;
      }>>(`/organizations/${organizationId}/public-portal-key`);
      return response.data;
    } catch (error) {
      return this.handleError<{
        publicPortalKey: string;
        maskedPublicPortalKey: string;
        createdAt: string;
      }>(error);
    }
  }

  async revokePublicPortalKey(organizationId: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/organizations/${organizationId}/public-portal-key`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getPublicPortalKeyStatus(organizationId: string): Promise<ApiResponse<{
    hasPublicPortalKey: boolean;
    maskedPublicPortalKey: string | null;
    createdAt: string | null;
  }>> {
    try {
      const response = await this.api.get<ApiResponse<{
        hasPublicPortalKey: boolean;
        maskedPublicPortalKey: string | null;
        createdAt: string | null;
      }>>(`/organizations/${organizationId}/public-portal-key/status`);
      return response.data;
    } catch (error) {
      return this.handleError<{
        hasPublicPortalKey: boolean;
        maskedPublicPortalKey: string | null;
        createdAt: string | null;
      }>(error);
    }
  }

  // Lookup endpoints
  async getStatuses(): Promise<ApiResponse<StatusLookupResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<StatusLookupResponse[]>>('/lookups/statuses');
      return response.data;
    } catch (error) {
      return this.handleError<StatusLookupResponse[]>(error);
    }
  }

  async getPriorities(): Promise<ApiResponse<PriorityLookupResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<PriorityLookupResponse[]>>('/lookups/priorities');
      return response.data;
    } catch (error) {
      return this.handleError<PriorityLookupResponse[]>(error);
    }
  }

  async getArticleStatuses(): Promise<ApiResponse<ArticleStatusLookupResponse[]>> {
    try {
      const response = await this.api.get<ApiResponse<ArticleStatusLookupResponse[]>>('/lookups/article-statuses');
      return response.data;
    } catch (error) {
      return this.handleError<ArticleStatusLookupResponse[]>(error);
    }
  }

  async getAllLookups(): Promise<ApiResponse<LookupsResponse>> {
    try {
      const response = await this.api.get<ApiResponse<LookupsResponse>>('/lookups');
      return response.data;
    } catch (error) {
      return this.handleError<LookupsResponse>(error);
    }
  }

  // ============================================
  // Notifications API
  // ============================================

  async getNotifications(filters?: NotificationFilterRequest): Promise<ApiResponse<NotificationListResponse>> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const queryString = params.toString();
      const url = queryString ? `/notifications?${queryString}` : '/notifications';

      const response = await this.api.get<ApiResponse<NotificationListResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<NotificationListResponse>(error);
    }
  }

  async getNotificationCount(): Promise<ApiResponse<NotificationCountResponse>> {
    try {
      const response = await this.api.get<ApiResponse<NotificationCountResponse>>('/notifications/count');
      return response.data;
    } catch (error) {
      return this.handleError<NotificationCountResponse>(error);
    }
  }

  async getNotification(id: string): Promise<ApiResponse<NotificationResponse>> {
    try {
      const response = await this.api.get<ApiResponse<NotificationResponse>>(`/notifications/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<NotificationResponse>(error);
    }
  }

  async markNotificationAsRead(id: string): Promise<ApiResponse<boolean>> {
    try {
      const deviceEndpoint = await this.getDeviceEndpoint();
      const headers: Record<string, string> = {};
      if (deviceEndpoint) {
        headers['X-Device-Endpoint'] = deviceEndpoint;
      }
      const response = await this.api.post<ApiResponse<boolean>>(`/notifications/${id}/read`, undefined, { headers });
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async markAllNotificationsAsRead(): Promise<ApiResponse<number>> {
    try {
      const deviceEndpoint = await this.getDeviceEndpoint();
      const headers: Record<string, string> = {};
      if (deviceEndpoint) {
        headers['X-Device-Endpoint'] = deviceEndpoint;
      }
      const response = await this.api.post<ApiResponse<number>>('/notifications/read-all', undefined, { headers });
      return response.data;
    } catch (error) {
      return this.handleError<number>(error);
    }
  }

  async markMultipleNotificationsAsRead(request: MarkNotificationsReadRequest): Promise<ApiResponse<number>> {
    try {
      const deviceEndpoint = await this.getDeviceEndpoint();
      const headers: Record<string, string> = {};
      if (deviceEndpoint) {
        headers['X-Device-Endpoint'] = deviceEndpoint;
      }
      const response = await this.api.post<ApiResponse<number>>('/notifications/read-multiple', request, { headers });
      return response.data;
    } catch (error) {
      return this.handleError<number>(error);
    }
  }

  async deleteNotification(id: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.delete<ApiResponse<boolean>>(`/notifications/${id}`);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async deleteAllReadNotifications(): Promise<ApiResponse<number>> {
    try {
      const response = await this.api.delete<ApiResponse<number>>('/notifications/read');
      return response.data;
    } catch (error) {
      return this.handleError<number>(error);
    }
  }

  // ============================================
  // Push Subscriptions API
  // ============================================

  async getVapidPublicKey(): Promise<ApiResponse<{ publicKey: string }>> {
    try {
      const response = await this.api.get<ApiResponse<{ publicKey: string }>>('/pushsubscriptions/vapid-public-key');
      return response.data;
    } catch (error) {
      return this.handleError<{ publicKey: string }>(error);
    }
  }

  async subscribeToPush(request: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    deviceName?: string;
    userAgent?: string;
  }): Promise<ApiResponse<{
    id: string;
    endpoint: string;
    deviceName: string | null;
    createdAt: string;
    lastUsedAt: string | null;
    isActive: boolean;
  }>> {
    try {
      const response = await this.api.post<ApiResponse<{
        id: string;
        endpoint: string;
        deviceName: string | null;
        createdAt: string;
        lastUsedAt: string | null;
        isActive: boolean;
      }>>('/pushsubscriptions/subscribe', request);
      return response.data;
    } catch (error) {
      return this.handleError<{
        id: string;
        endpoint: string;
        deviceName: string | null;
        createdAt: string;
        lastUsedAt: string | null;
        isActive: boolean;
      }>(error);
    }
  }

  async unsubscribeFromPush(endpoint: string): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>('/pushsubscriptions/unsubscribe', { endpoint });
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getPushSubscriptions(): Promise<ApiResponse<Array<{
    id: string;
    endpoint: string;
    deviceName: string | null;
    createdAt: string;
    lastUsedAt: string | null;
    isActive: boolean;
  }>>> {
    try {
      const response = await this.api.get<ApiResponse<Array<{
        id: string;
        endpoint: string;
        deviceName: string | null;
        createdAt: string;
        lastUsedAt: string | null;
        isActive: boolean;
      }>>>('/pushsubscriptions');
      return response.data;
    } catch (error) {
      return this.handleError<Array<{
        id: string;
        endpoint: string;
        deviceName: string | null;
        createdAt: string;
        lastUsedAt: string | null;
        isActive: boolean;
      }>>(error);
    }
  }

  // ============================================
  // Automation Rules API
  // ============================================

  async getAutomationRules(filters?: AutomationRuleFilterRequest, organizationId?: string): Promise<ApiResponse<AutomationRuleListResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      const queryString = params.toString();
      const url = queryString ? `/automation-rules?${queryString}` : '/automation-rules';
      const response = await this.api.get<ApiResponse<AutomationRuleListResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<AutomationRuleListResponse>(error);
    }
  }

  async getAutomationRule(id: string, organizationId?: string): Promise<ApiResponse<AutomationRuleDetailResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/${id}?${queryString}` : `/automation-rules/${id}`;
      const response = await this.api.get<ApiResponse<AutomationRuleDetailResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<AutomationRuleDetailResponse>(error);
    }
  }

  async createAutomationRule(request: CreateAutomationRuleRequest, organizationId?: string): Promise<ApiResponse<AutomationRuleDetailResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules?${queryString}` : '/automation-rules';
      const response = await this.api.post<ApiResponse<AutomationRuleDetailResponse>>(url, request);
      return response.data;
    } catch (error) {
      return this.handleError<AutomationRuleDetailResponse>(error);
    }
  }

  async updateAutomationRule(id: string, request: UpdateAutomationRuleRequest, organizationId?: string): Promise<ApiResponse<AutomationRuleDetailResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/${id}?${queryString}` : `/automation-rules/${id}`;
      const response = await this.api.put<ApiResponse<AutomationRuleDetailResponse>>(url, request);
      return response.data;
    } catch (error) {
      return this.handleError<AutomationRuleDetailResponse>(error);
    }
  }

  async deleteAutomationRule(id: string, organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/${id}?${queryString}` : `/automation-rules/${id}`;
      const response = await this.api.delete<ApiResponse<boolean>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async toggleAutomationRule(id: string, request: ToggleAutomationRuleRequest, organizationId?: string): Promise<ApiResponse<AutomationRuleDetailResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/${id}/toggle?${queryString}` : `/automation-rules/${id}/toggle`;
      const response = await this.api.patch<ApiResponse<AutomationRuleDetailResponse>>(url, request);
      return response.data;
    } catch (error) {
      return this.handleError<AutomationRuleDetailResponse>(error);
    }
  }

  async reorderAutomationRules(items: RuleOrderItem[], organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/reorder?${queryString}` : '/automation-rules/reorder';
      const response = await this.api.put<ApiResponse<boolean>>(url, items);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  async getAutomationRuleLogs(ruleId: string, filters?: AutomationRuleLogFilterRequest, organizationId?: string): Promise<ApiResponse<AutomationRuleLogListResponse>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/${ruleId}/logs?${queryString}` : `/automation-rules/${ruleId}/logs`;
      const response = await this.api.get<ApiResponse<AutomationRuleLogListResponse>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<AutomationRuleLogListResponse>(error);
    }
  }

  async seedDefaultAutomationRules(organizationId?: string): Promise<ApiResponse<boolean>> {
    try {
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId);
      const queryString = params.toString();
      const url = queryString ? `/automation-rules/seed-defaults?${queryString}` : '/automation-rules/seed-defaults';
      const response = await this.api.post<ApiResponse<boolean>>(url);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Contact form (public, no auth required)
  async submitContactForm(data: { name: string; email: string; organization?: string; message: string }): Promise<ApiResponse<boolean>> {
    try {
      const response = await this.api.post<ApiResponse<boolean>>('/contact-form', data);
      return response.data;
    } catch (error) {
      return this.handleError<boolean>(error);
    }
  }

  // Blob requests get the server's JSON error body back as a Blob too, which handleError can't read
  private async getBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
    const data = axios.isAxiosError(error) ? error.response?.data : undefined;
    if (data instanceof Blob && data.type.includes('json')) {
      try {
        return getApiErrorMessage(JSON.parse(await data.text()) as ApiResponse<unknown>, fallback);
      } catch {
        // Unparseable body - fall back to the status-based message below
      }
    }
    return getApiErrorMessage(this.handleError(error), fallback);
  }

  private handleError<T>(error: unknown): ApiResponse<T> {
    const response = axios.isAxiosError(error) ? error.response : undefined;

    // The API's ApiResponse body carries the real reason - pass it through with the HTTP status code.
    // (A Blob body - from a download request - isn't parsed here; see getBlobErrorMessage.)
    if (response?.data && typeof response.data === 'object' && !(response.data instanceof Blob)) {
      return {
        ...(response.data as Record<string, unknown>),
        statusCode: response.status
      } as ApiResponse<T>;
    }

    // No ApiResponse body (network failure, rate limiter, gateway error). Axios's own messages
    // ("Network Error", "Request failed with status code 502") aren't meant for end users.
    const statusCode = response?.status ?? 0;
    const message = axios.isAxiosError(error) && !response
      ? 'Unable to reach the server. Check your connection and try again.'
      : statusCode === 429
        ? 'Too many requests. Please wait a moment and try again.'
        : statusCode === 413
          ? 'The upload is too large. Please choose a smaller file.'
          : statusCode === 403
            // Authorization policies reject with an empty body; retrying won't help
            ? "You don't have permission to do that."
            : statusCode === 404
              ? 'The requested item could not be found.'
              : 'An unexpected error occurred. Please try again.';

    return {
      success: false,
      message,
      errors: [message],
      statusCode,
    };
  }
}

export const apiService = new ApiService();